/**
 * What Nexus knows, counted from what Nexus actually holds.
 *
 * Every number here is read from a governed endpoint. There is no summary
 * service and no cached dashboard table, because a second place that stores
 * "how many applications are there" is a second source of truth for a question
 * the registry already answers.
 *
 * Which is also why this page is shorter than it could be. Areas the platform
 * has not built yet are absent rather than shown as zero — a zero is a claim,
 * and "we have not built that" is not the same claim.
 *
 * ## What went wrong on a quiet tenant (`NXP-WEB-PROTOTYPE-FIDELITY-002`)
 *
 * The shell converged on the v2.3 product first — workspace-first navigation,
 * hubs that survey a cluster — and then a real sparse principal signed in and
 * landed here, on a sentence announcing that Nexus had nothing to report and
 * one utilitarian card under it. The tenant genuinely was quiet. The page still
 * read as broken, and those are different claims too.
 *
 * The fix is not to fill the screen. Nothing may be invented here: no sample
 * metric, no fabricated activity, no recommendation, no disabled card standing
 * in for a capability that does not exist. What changed is that Home now
 * answers, from what it already had, the questions a person arriving actually
 * has:
 *
 * ```
 * where am I          /identity/me, and the scope the answers were computed in
 * what can I reach    the capability projection, through the one eligibility fn
 * what is held        the four governed reads this page already made
 * what happened       the audit trail, when the caller may read it
 * ```
 *
 * Home *is* the Home workspace — the registry routes that workspace to `/` —
 * so it is built from the hub's own grammar rather than from a second design
 * system invented for the landing page.
 *
 * ## The silences
 *
 * On a tenant where nothing has been granted, every card drops out and what is
 * left must still be a page. Several different silences are possible here and
 * they are not the same news:
 *
 * ```
 * nothing granted      the enterprise may be full; you cannot see it
 * nothing summarized   you can reach things; none of them is what Home counts
 * granted, all zero    the enterprise is genuinely empty
 * refused outright     the decision point said no to the read itself
 * ```
 *
 * The first two used to arrive under one headline, which left the reader to
 * work out from a paragraph which of two very different things had happened.
 * They are now different sections of the page, which is a difference you can
 * see before you have read anything.
 *
 * ## Sections read their own endpoints
 *
 * The report and the activity trail are separate reads. Home used to gather
 * everything into one `Promise.all`, so a single refusal — a membership revoked
 * between the projection and the request — blanked the entire page. A refusal
 * belongs to the section that was refused.
 */

import { useCallback, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import type {
  AuditResult,
  CanonicalEntity,
  ConnectedSystem,
  OutboxHealth,
  Page,
  Principal,
} from "../api/types";
import { useResource, type Resource } from "../api/useResource";
import { Card, CorrelationLink, outcomeTone, Pill, when, type Tone } from "../components/primitives";
import { eligibleWorkspaces, type EligibleWorkspace } from "../components/navigation";
import { Empty, Show } from "../components/states";
import { useSession } from "../session/SessionProvider";

/**
 * A count, and whether the registry stopped counting.
 *
 * `limit=200` and `has_more` means two hundred is where the page ended, not
 * how many exist. Rendering the one as the other is the smallest possible
 * fabricated metric, and it is still a fabricated metric.
 */
interface Count {
  value: number;
  capped: boolean;
}

/** How many records the trail returns to Home. Enough to be a report, not a screen. */
const ACTIVITY_WINDOW = 6;

/** How many of one kind the registry holds, as far as one page can say. */
async function countOf(entityType: string, signal: AbortSignal): Promise<Count> {
  const page = await api.get<Page<CanonicalEntity>>("/core/entities", {
    params: { entity_type: entityType, status: "active", limit: 200 },
    signal,
  });
  return { value: page.items.length, capped: page.has_more };
}

/** An exact figure the platform reported, which no page limit truncated. */
function exact(value: number): Count {
  return { value, capped: false };
}

function Stat({ value, label, to }: { value: Count; label: string; to?: string }) {
  const body = (
    <span>
      <span className="nx-stat__value">
        {`${value.value.toLocaleString()}${value.capped ? "+" : ""}`}
      </span>
      <span className="nx-stat__label" style={{ display: "block" }}>
        {label}
      </span>
    </span>
  );
  return <div>{to ? <Link to={to}>{body}</Link> : body}</div>;
}

/** A section of Home, named so it can be moved between rather than scrolled past. */
function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="nx-hub__cluster" aria-labelledby={id}>
      <h3 className="nx-hub__cluster-label" id={id}>
        {title}
      </h3>
      {children}
    </section>
  );
}

/**
 * The scope every answer on this page was computed in.
 *
 * Selecting no organization is the **narrowest** setting available, not the
 * broadest: the engine matches an `ORGANIZATION`-scoped policy only when the
 * query carries one, so with none selected every organization-scoped grant
 * stops matching and only tenant-wide ones remain. Wording it as breadth would
 * tell somebody they had widened their reach at the moment they narrowed it.
 */
function scopeNote(principal: Principal): string {
  if (principal.organization_ids.length === 0) {
    return "You belong to no organization in this tenant, so only tenant-wide grants apply.";
  }
  if (principal.active_organization_id === null) {
    return "No organization is selected, so only tenant-wide grants apply — the narrowest scope available. The selector at the top of the page changes it.";
  }
  return "Answers here are computed as the organization selected at the top of the page. Grants scoped to another organization do not apply.";
}

/**
 * A workspace as somewhere to go from Home.
 *
 * Built from `eligibleWorkspaces` and nothing else. Home decides no
 * permission: a second implementation of "may this be offered" would
 * eventually disagree with the sidebar, and the more permissive one would be
 * the defect. The destination names inside are text rather than links — the
 * tile's overlay makes the whole card one target, and a link underneath it
 * would be a control nobody can reach.
 */
function WorkspaceEntry({ workspace }: { workspace: EligibleWorkspace }) {
  const reachable = workspace.groups.flatMap((group) => group.items.map((item) => item.label));
  return (
    <article className="nx-tile">
      <h4 className="nx-tile__name">
        <Link className="nx-tile__link" to={workspace.to}>
          {workspace.label}
        </Link>
      </h4>
      <p className="nx-tile__summary">{workspace.summary}</p>
      <p className="nx-tile__meta">{reachable.join(" · ")}</p>
    </article>
  );
}

/**
 * What the platform recorded, as the trail returned it.
 *
 * Its own read, so a refusal here refuses this section rather than the page.
 * Records the caller may not read are withheld whole by the endpoint and
 * counted; the count is reported and what they are is not, because describing
 * withheld evidence discloses the thing withholding it was for.
 */
function Activity() {
  const read = useCallback(
    (signal: AbortSignal) =>
      api.get<AuditResult>("/audit/events", { params: { limit: ACTIVITY_WINDOW }, signal }),
    [],
  );
  const { state, reload } = useResource<AuditResult>(read);

  return (
    <Show
      resource={state}
      what="the evidence trail"
      onRetry={reload}
      empty={(result) =>
        result.items.length === 0 ? (
          <Card>
            <Empty
              title="Nothing has been recorded here yet that you may read."
              hint="Activity appears as Nexus records it. There is nothing to catch up on."
            />
          </Card>
        ) : null
      }
    >
      {(result) => (
        <Card
          headingLevel={4}
          actions={
            // A link that reads as a link. `nx-hint` would grey it into the
            // card header and leave the only way deeper looking like a caption.
            <Link to="/operations/audit">Open the evidence trail</Link>
          }
          title="Newest first"
        >
          <ul className="nx-home__activity">
            {result.items.map((record) => (
              <li key={record.audit_event_id}>
                <span className="nx-home__activity-what">
                  <strong>{record.operation ?? record.event_type}</strong>
                  <Pill tone={outcomeTone(record.outcome)}>{record.outcome}</Pill>
                </span>
                <span className="nx-hint">
                  {when(record.occurred_at)}
                  {record.correlation_id ? (
                    <>
                      {" · "}
                      <CorrelationLink value={record.correlation_id} />
                    </>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
          {result.completeness === "complete" ? null : (
            <p className="nx-hint" style={{ marginTop: 12 }}>
              {result.withheld} further record{result.withheld === 1 ? " is" : "s are"} in this
              window and not visible under your current authorization. What they are is
              deliberately not described.
            </p>
          )}
        </Card>
      )}
    </Show>
  );
}

/**
 * One Home source, in a card that stays named whatever the source does.
 *
 * The frame is rendered outside `Show` deliberately. A source that could not be
 * read must still say *which* source could not be read, and a page that
 * replaces the whole card with a bare outage message has told the reader that
 * something failed without telling them what.
 */
function Source<T>({
  title,
  what,
  resource,
  onRetry,
  children,
}: {
  title: string;
  what: string;
  resource: Resource<T>;
  onRetry: () => void;
  children: (value: T) => ReactNode;
}) {
  return (
    // Each of Home's cards sits inside a `Section`, whose label is an h3, so
    // the shared h2 default would describe a structure the page does not have.
    <Card title={title} headingLevel={4}>
      <Show resource={resource} what={what} onRetry={onRetry}>
        {children}
      </Show>
    </Card>
  );
}

interface EntityCounts {
  organizations: Count;
  products: Count;
  applications: Count;
  repositories: Count;
}

/**
 * What the canonical registry holds.
 *
 * Four queries and one source: same endpoint, same capability, one card. They
 * share a failure because they *are* one read of one registry — which is a
 * different thing from the three cards sharing one, and is why that stopped.
 */
function EnterpriseCard() {
  const read = useCallback(async (signal: AbortSignal): Promise<EntityCounts> => {
    const [organizations, products, applications, repositories] = await Promise.all([
      countOf("organization", signal),
      countOf("product", signal),
      countOf("application", signal),
      countOf("repository", signal),
    ]);
    return { organizations, products, applications, repositories };
  }, []);
  const { state, reload } = useResource<EntityCounts>(read);

  return (
    <Source title="Enterprise" what="the entity registry" resource={state} onRetry={reload}>
      {(counts) => (
        <>
          <div className="nx-home__stats">
            <Stat value={counts.products} label="Products" to="/enterprise/graph" />
            <Stat value={counts.applications} label="Applications" to="/enterprise/graph" />
            <Stat value={counts.repositories} label="Repositories" to="/enterprise/graph" />
          </div>
          <p className="nx-hint" style={{ marginTop: 10 }}>
            {/* The rule the stats above already follow, applied to the sentence
                below them: `limit=200` with `has_more` is where the page ended,
                not how many exist. Stating it here as an exact figure would put
                back, in prose, the fabricated metric the stats refuse. */}
            {counts.organizations.value}
            {counts.organizations.capped ? "+" : ""} organization
            {counts.organizations.value === 1 && !counts.organizations.capped ? "" : "s"} ·
            relationships may be many-to-many, and who governs is resolved separately.
          </p>
        </>
      )}
    </Source>
  );
}

/** What Nexus integrates with. Its own read, so its own silence. */
function IntegrationCard() {
  const read = useCallback(
    (signal: AbortSignal): Promise<Count> =>
      api
        .get<Page<ConnectedSystem>>("/core/connected-systems", { params: { limit: 200 }, signal })
        .then((page) => ({ value: page.items.length, capped: page.has_more })),
    [],
  );
  const { state, reload } = useResource<Count>(read);

  return (
    <Source title="Integration" what="connected systems" resource={state} onRetry={reload}>
      {(systems) => (
        <>
          <div className="nx-home__stats">
            <Stat value={systems} label="Connected systems" to="/enterprise/systems" />
          </div>
          <p className="nx-hint" style={{ marginTop: 10 }}>
            Something Nexus talks to — deliberately not the same object as a canonical
            Application.
          </p>
        </>
      )}
    </Source>
  );
}

/**
 * What the backbone still owes.
 *
 * These figures are exact and stay exact: the health endpoint counts with SQL
 * aggregates over the whole tenant rather than returning a page, so there is no
 * limit for one of them to have been truncated by.
 */
function OperationsCard() {
  const read = useCallback(
    (signal: AbortSignal) => api.get<OutboxHealth>("/core/events/outbox/health", { signal }),
    [],
  );
  const { state, reload } = useResource<OutboxHealth>(read);

  return (
    <Source title="Operations" what="the event backbone" resource={state} onRetry={reload}>
      {(backbone) => {
        const tone: Tone =
          backbone.dead_lettered > 0 ? "bad" : backbone.retrying > 0 ? "wait" : "ok";
        return (
          <>
            <div className="nx-home__stats">
              <Stat value={exact(backbone.pending)} label="Pending" to="/operations/events" />
              <Stat value={exact(backbone.retrying)} label="Retrying" to="/operations/events" />
              <Stat
                value={exact(backbone.dead_lettered)}
                label="Dead lettered"
                to="/operations/events"
              />
            </div>
            <p style={{ marginTop: 10 }}>
              <Pill tone={tone}>
                {backbone.transport_enabled ? "Transport enabled" : "Transport not enabled"}
              </Pill>
              <span className="nx-hint"> Last published {when(backbone.last_published_at)}</span>
            </p>
          </>
        );
      }}
    </Source>
  );
}

export function Home() {
  const session = useSession();

  // What the caller may see at all. Not a permission check — the decision point
  // already made these decisions and this only reads them back — but it is what
  // separates "you were shown nothing because nothing was granted" from "you
  // were shown nothing because there is nothing". A source whose capability is
  // absent is never mounted, so its endpoint is never called: the surface is
  // absent because it was not granted, not because a refusal came back.
  const counts = {
    entities: session.may("entity.read"),
    systems: session.may("system.read"),
    backbone: session.may("outbox.read"),
  };
  const summarizes = counts.entities || counts.systems || counts.backbone;

  // The one implementation of eligibility, shared with the shell, the hubs and
  // the breadcrumb. Home's own workspace is dropped: it is this page.
  const destinations = eligibleWorkspaces(session.may).filter(
    (workspace) => workspace.key !== "home",
  );

  return (
    <div className="nx-hub nx-home">
      <header className="nx-hub__head">
        <p className="nx-hub__eyebrow">Taevas Nexus</p>
        {/* An `h2`: the route title in the shell is the page's `h1`. The name
            is the one `/identity/me` published — this page introduces no
            second idea of who is signed in. */}
        <h2 className="nx-hub__title">Welcome, {session.principal.display_name}.</h2>
        <p className="nx-hub__summary">
          Home reports where you can go and what Nexus holds for you. Everything on it is read
          from a governed endpoint, so an area you have not been granted is absent rather than
          shown as empty.
        </p>
        <p className="nx-hint nx-home__scope">{scopeNote(session.principal)}</p>
      </header>

      {destinations.length > 0 ? (
        <Section id="nx-home-go" title="Where you can go">
          <div className="nx-tiles">
            {destinations.map((workspace) => (
              <WorkspaceEntry key={workspace.key} workspace={workspace} />
            ))}
          </div>
        </Section>
      ) : null}

      {destinations.length === 0 && !summarizes ? (
        // Nothing anywhere. Truthful, and deliberately not dressed up: no
        // disabled tiles for areas that were never offered, and no invitation
        // to do something this account cannot do.
        <Section id="nx-home-access" title="Your access">
          <div className="nx-home__note">
            <Card title="No area of Nexus has been granted to you yet" headingLevel={4}>
              <p>
                You are signed in, and nothing in this tenant has been granted to the organization
                you are acting as. Nothing was refused and nothing failed — there is simply nothing
                this account may open yet.
              </p>
              <p className="nx-hint" style={{ marginTop: 10 }}>
                Access is granted per operation, and reading is granted apart from changing. Ask
                whoever administers roles for this tenant.
              </p>
            </Card>
          </div>
        </Section>
      ) : (
        <Section id="nx-home-holds" title="What Nexus holds">
          {summarizes ? (
            // Three sources, three reads, three states. They used to be
            // gathered into one `Promise.all`, which meant one of them being
            // unreachable discarded the other two answers after they had
            // already come back 200 — reporting three things as unreadable
            // when one was (`NXP-WEB-PROTOTYPE-FIDELITY-002`). A source now
            // fails alone, inside the card that names it.
            <div className="nx-grid">
              {counts.entities ? <EnterpriseCard /> : null}
              {counts.systems ? <IntegrationCard /> : null}
              {counts.backbone ? <OperationsCard /> : null}
            </div>
          ) : (
            <div className="nx-home__note">
              <Card title="Nothing for Home to summarize yet" headingLevel={4}>
                <p>
                  Home counts canonical entities, connected systems and the event backbone. None of
                  those has been granted to you in the organization you are acting as, so there is
                  nothing here for it to count. That is a statement about this summary, not about
                  your access — what you may reach is above.
                </p>
                <p className="nx-hint" style={{ marginTop: 10 }}>
                  Access is granted per operation, and reading is granted apart from changing.
                </p>
              </Card>
            </div>
          )}
        </Section>
      )}

      {session.may("audit.read") ? (
        <Section id="nx-home-activity" title="Recent activity">
          <Activity />
        </Section>
      ) : null}
    </div>
  );
}
